// Utilidades compartidas del módulo Control Bancario (Tesorería).
// CRUD directo a PostgREST vía el atajo /rest/control_bancario.<tabla> (mismo
// patrón que features/pos/shared.tsx e inventario/shared.tsx) + helper RPC.
import { ReactNode, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Construction } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import { PanelIntegrado } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

// Ítem de catálogo con etiqueta traducida (getter: respeta el cambio de idioma).
const cat = (g: string, v: string) => ({ v, get l() { return i18n.t(`bancos:cat.${g}.${v}`); } });

const base = (t: string) => `/rest/control_bancario.${t}`;
const repr = { headers: { Prefer: 'return=representation' } };

// CRUD con borrado LÓGICO (deleted_at) — nunca se borran físicamente bancos/cuentas.
export const cb = {
  list: async <T,>(t: string, query = ''): Promise<T[]> => (await apiClient.get<T[]>(`${base(t)}${query}`)).data,
  create: async (t: string, body: unknown) => (await apiClient.post(base(t), body, repr)).data,
  update: async (t: string, id: string, body: unknown) => (await apiClient.patch(`${base(t)}?id=eq.${id}`, body, repr)).data,
  remove: async (t: string, id: string) =>
    (await apiClient.patch(`${base(t)}?id=eq.${id}`, { deleted_at: new Date().toISOString() }, repr)).data,
};

// RPC del esquema (Content-Profile: control_bancario).
const CB = { headers: { 'Content-Profile': 'control_bancario', 'Accept-Profile': 'control_bancario' } };
export async function cbRpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  return (await apiClient.post<T>(`/rpc/${fn}`, args, CB)).data;
}

export const money = (n: number | null | undefined, moneda = 'USD') =>
  new Intl.NumberFormat(LOCALE, { style: 'currency', currency: moneda }).format(Number(n ?? 0));

export const fieldCls =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function Campo({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block space-y-1 ${className}`}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-input" />
      {label}
    </label>
  );
}

// Regla del proyecto (2026-10-10): sin ventanas modales. «Modal» se conserva por
// compatibilidad, pero muestra un panel integrado en la página.
export function Modal({ title, onClose, children, wide = false }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return <PanelIntegrado title={title} onClose={onClose} wide={wide}>{children}</PanelIntegrado>;
}

// Pantalla placeholder para las opciones del módulo aún no implementadas.
export function EnConstruccionCB({ titulo }: { titulo: string }) {
  const { t } = useTranslation('bancos');
  return (
    <div className="p-6">
      <h1 className="text-xl font-semibold mb-1">{titulo}</h1>
      <p className="mb-4 text-sm text-muted-foreground">{t('shared.modulo_sub')}</p>
      <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed py-16 text-center text-muted-foreground">
        <Construction className="w-10 h-10" />
        <p className="text-sm">{t('shared.proxima_fase')}</p>
        <p className="text-xs">{t('shared.estructura')}</p>
      </div>
    </div>
  );
}

// -------------------------------------------------------------- Tipos --------
export interface Banco {
  id: string; codigo: string | null; nombre: string; nombre_corto: string | null;
  tipo: string; swift: string | null; pais: string; es_activo: boolean; notas: string | null;
}
export interface Cuenta {
  id: string; banco_id: string; numero: string; tipo: string; moneda: string;
  alias: string | null; titular: string | null; cuenta_contable_id: string | null;
  saldo_inicial: number; fecha_saldo_inicial: string | null; sobregiro_autorizado: number;
  es_activo: boolean; notas: string | null;
}
export interface PlanCuenta { id: string; codigo: string; nombre: string }
export interface MapeoContable { concepto: string; label: string; cuenta_id: string | null; codigo: string | null; nombre: string | null }
export interface Parametros {
  id: string; moneda_base: string; decimales: number; permite_sobregiro: boolean;
  dias_alerta_cheque: number; requiere_aprobacion_pagos: boolean; monto_max_sin_aprobacion: number; nivel_min_aprobador: number;
  prefijo_orden_pago: string; siguiente_orden_pago: number;
  concil_tolerancia_dias: number; concil_tolerancia_monto: number;
}
// Config SMTP del módulo (RPC cb_smtp_get / cb_smtp_set). La clave nunca se devuelve.
export interface SmtpConfigData {
  host?: string | null; puerto?: number | null; seguro?: boolean; usuario?: string | null;
  from_email?: string | null; from_nombre?: string | null; activo: boolean; password_configurada: boolean;
}
// Orden de pago pendiente de aprobar (bandeja del aprobador — RPC cb_ordenes_por_aprobar).
export interface OrdenPorAprobar {
  orden_id: string; numero: string; fecha: string; beneficiario: string; monto: number; moneda: string;
  forma_pago: string; concepto: string | null; documento_ref: string | null;
  creado_por: string | null; creado_at: string; ticket_buy_id: string | null; serie_factura: string | null;
}
export interface ReglaConciliacion {
  id: string; nombre: string; prioridad: number; activo: boolean;
  match_monto: string; tolerancia_monto: number; tolerancia_dias: number;
  match_por: string; patron: string | null; descripcion: string | null;
}
export interface FormatoExtracto {
  id: string; nombre: string; banco_id: string | null; tipo: string;
  delimitador: string; tiene_encabezado: boolean; formato_fecha: string;
  col_fecha: string | null; col_descripcion: string | null; col_referencia: string | null;
  col_debito: string | null; col_credito: string | null; col_saldo: string | null; activo: boolean;
}

// ----------------------------------------------------------- Catálogos -------
export const TIPO_BANCO = [cat('tipo_banco', 'banco'), cat('tipo_banco', 'cooperativa'), cat('tipo_banco', 'mutualista'), cat('tipo_banco', 'emisor_tarjeta'), cat('tipo_banco', 'otro')];
export const TIPO_CUENTA = [cat('tipo_cuenta', 'corriente'), cat('tipo_cuenta', 'ahorros'), cat('tipo_cuenta', 'monetaria')];
export const MATCH_MONTO = [cat('match_monto', 'exacto'), cat('match_monto', 'tolerancia')];
export const MATCH_POR = [cat('match_por', 'referencia'), cat('match_por', 'documento'), cat('match_por', 'glosa'), cat('match_por', 'monto_fecha')];
export const TIPO_FORMATO = [
  { v: 'csv', l: 'CSV' }, { v: 'ofx', l: 'OFX' }, { v: 'mt940', l: 'MT940' }, { v: 'xlsx', l: 'Excel (XLSX)' },
];
export const CATEGORIA_MOV = [cat('categoria_mov', 'cobro'), cat('categoria_mov', 'pago'), cat('categoria_mov', 'transferencia'), cat('categoria_mov', 'comision'), cat('categoria_mov', 'interes'), cat('categoria_mov', 'nota_debito'), cat('categoria_mov', 'nota_credito'), cat('categoria_mov', 'cheque'), cat('categoria_mov', 'deposito'), cat('categoria_mov', 'impuesto'), cat('categoria_mov', 'prestamo'), cat('categoria_mov', 'otro')];
export const label = (arr: { v: string; l: string }[], v: string | null) => arr.find((x) => x.v === v)?.l ?? (v ?? '—');

// ---- Ordenamiento de tablas (asc/desc por columna) -------------------------
export type SortDir = 'asc' | 'desc';
export interface SortState { key: string; dir: SortDir; toggle: (k: string) => void }
export function useSort<T>(rows: T[], accessor: (row: T, key: string) => string | number | null,
                           initialKey = '', initialDir: SortDir = 'asc') {
  const [key, setKey] = useState(initialKey);
  const [dir, setDir] = useState<SortDir>(initialDir);
  const toggle = (k: string) => { if (k === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc')); else { setKey(k); setDir('asc'); } };
  const sorted = useMemo(() => {
    if (!key) return rows;
    const arr = [...rows];
    arr.sort((a, b) => {
      const va = accessor(a, key); const vb = accessor(b, key);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;   // nulos al final
      if (vb == null) return -1;
      const c = (typeof va === 'number' && typeof vb === 'number')
        ? va - vb
        : String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' });
      return dir === 'asc' ? c : -c;
    });
    return arr;
  }, [rows, key, dir, accessor]);
  return { sorted, key, dir, toggle } as { sorted: T[] } & SortState;
}

export function SortTh({ children, col, sort, align = 'left', className = '' }:
  { children: ReactNode; col: string; sort: SortState; align?: 'left' | 'right' | 'center'; className?: string }) {
  const { t } = useTranslation('bancos');
  const active = sort.key === col;
  const alignCls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th className={`py-2 pr-3 ${alignCls} cursor-pointer select-none hover:text-foreground ${className}`}
        onClick={() => sort.toggle(col)} title={t('shared.ordenar')}>
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {children}
        <span className={`text-[10px] leading-none ${active ? 'text-foreground' : 'text-muted-foreground/40'}`}>
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </span>
    </th>
  );
}

export interface Movimiento {
  id: string; fecha: string; fecha_valor: string | null; tipo: 'credito' | 'debito'; monto: number;
  concepto: string | null; referencia: string | null; categoria: string | null;
  estado: 'registrado' | 'conciliado' | 'anulado'; origen: string; documento_ref: string | null; saldo: number;
  contabilizado?: boolean;
}
export interface LibroBanco {
  cuenta: { id: string; alias: string | null; numero: string; banco: string; banco_corto: string; moneda: string; saldo_inicial: number };
  saldo_inicial_periodo: number;
  movimientos: Movimiento[];
  totales: { creditos: number; debitos: number; n: number };
  saldo_final: number;
}
export interface CuentaMini { id: string; alias: string | null; numero: string; moneda: string; banco?: { nombre: string } }

export interface MovPend { id: string; fecha: string; tipo: 'credito' | 'debito'; monto: number; concepto: string | null; referencia: string | null; origen: string; }
export interface LinPend { id: string; fecha: string; tipo: 'credito' | 'debito'; monto: number; concepto: string | null; referencia: string | null; }
export interface Emparejado { linea_id: string; movimiento_id: string; fecha: string; tipo: string; monto: number; concepto: string | null; referencia: string | null; mov_concepto: string | null; mov_origen: string; }
export interface EstadoConcil {
  cuenta: { id: string; alias: string | null; numero: string; banco: string; banco_corto: string; moneda: string };
  saldo_libro: number; saldo_extracto: number; diferencia: number;
  movimientos_pendientes: MovPend[]; lineas_pendientes: LinPend[]; emparejados: Emparejado[];
  totales: { mov_pendientes: number; lin_pendientes: number; emparejados: number };
}

export interface Chequera {
  id: string; cuenta_id: string; descripcion: string | null; prefijo: string | null;
  numero_desde: number; numero_hasta: number; siguiente: number; estado: string; activo: boolean;
}
export interface Cheque {
  id: string; cuenta_id: string; chequera_id: string | null; numero: number; beneficiario: string;
  monto: number; fecha_emision: string; fecha_cobro: string; concepto: string | null;
  estado: 'girado' | 'cobrado' | 'anulado' | 'protestado'; motivo: string | null;
  banco?: { nombre: string }; cuenta?: { alias: string | null; numero: string; moneda: string };
}
export const ESTADO_CHEQUE = [cat('estado_cheque', 'girado'), cat('estado_cheque', 'cobrado'), cat('estado_cheque', 'anulado'), cat('estado_cheque', 'protestado')];
export const ESTADO_CHEQUE_CLS: Record<string, string> = {
  girado: 'bg-pendiente-soft text-pendiente', cobrado: 'bg-estable-soft text-estable',
  anulado: 'bg-muted text-muted-foreground', protestado: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

export interface CajaChica {
  id: string; nombre: string; responsable: string | null; monto_asignado: number; estado: string;
  cuenta_id: string; cuenta: string; moneda: string; saldo_efectivo: number; total_pendiente: number; n_pendientes: number;
}
export interface CajaEstado {
  caja: { id: string; nombre: string; responsable: string | null; monto_asignado: number; estado: string; cuenta: string; moneda: string };
  saldo_efectivo: number; total_pendiente: number;
  vales_pendientes: { id: string; fecha: string; monto: number; categoria: string | null; beneficiario: string | null; concepto: string | null; comprobante: string | null }[];
}
export interface CajaVale {
  id: string; fecha: string; monto: number; categoria: string | null; beneficiario: string | null;
  concepto: string | null; comprobante: string | null; estado: string;
}
export const GASTO_CAT = [cat('gasto_cat', 'movilizacion'), cat('gasto_cat', 'suministros'), cat('gasto_cat', 'alimentacion'), cat('gasto_cat', 'mensajeria'), cat('gasto_cat', 'limpieza'), cat('gasto_cat', 'mantenimiento'), cat('gasto_cat', 'combustible'), cat('gasto_cat', 'varios')];

export const FORMA_PAGO = [cat('forma_pago', 'transferencia'), cat('forma_pago', 'cheque'), cat('forma_pago', 'efectivo'), cat('forma_pago', 'tarjeta'), cat('forma_pago', 'otro')];
export const FORMA_COBRO = [cat('forma_cobro', 'transferencia'), cat('forma_cobro', 'deposito'), cat('forma_cobro', 'cheque'), cat('forma_cobro', 'efectivo'), cat('forma_cobro', 'tarjeta'), cat('forma_cobro', 'otro')];
export interface PagoProgramado {
  id: string; fecha_vencimiento: string; beneficiario: string; monto: number; moneda: string;
  concepto: string | null; categoria: string | null; cuenta_id: string | null; documento_ref: string | null;
  prioridad: number; estado: 'programado' | 'pagado' | 'anulado'; ticket_buy_id?: string | null;
}
export interface OrdenPago {
  id: string; numero: string; fecha: string; beneficiario: string; monto: number; moneda: string;
  cuenta_id: string; forma_pago: string; concepto: string | null; documento_ref: string | null;
  estado: 'borrador' | 'aprobada' | 'pagada' | 'anulada'; motivo: string | null;
  cuenta?: { alias: string | null; numero: string }; ticket_buy_id?: string | null;
}

// Factura de compra (CxP) con saldo pendiente — para enlazar un pago al documento.
export interface CxpPendiente {
  ticket_id: string; serie: string | null; proveedor: string | null;
  fecha: string | null; vencimiento: string | null; total: number; abonado: number; saldo: number;
}

// Selector reutilizable de factura CxP pendiente (Órdenes de pago, Cheques).
// Al elegir una factura, el pago quedará enlazado y su saldo se rebajará al pagar.
export function CxpFacturaPicker({ value, onPick }: { value: string | null; onPick: (f: CxpPendiente | null) => void }) {
  const { t } = useTranslation('bancos');
  const [q, setQ] = useState('');
  const { data: rows = [] } = useQuery<CxpPendiente[]>({
    queryKey: ['cb-cxp-pend', q],
    queryFn: () => cbRpc<CxpPendiente[]>('cb_cxp_pendientes', { _busq: q || null }),
  });
  return (
    <div className="space-y-1">
      <input className={fieldCls} placeholder={t('shared.buscar_factura')} value={q} onChange={(e) => setQ(e.target.value)} />
      <select className={fieldCls} value={value ?? ''} onChange={(e) => onPick(rows.find((r) => r.ticket_id === e.target.value) ?? null)}>
        <option value="">{t('shared.sin_factura')}</option>
        {rows.map((r) => (
          <option key={r.ticket_id} value={r.ticket_id}>{t('shared.saldo_op', { serie: r.serie, proveedor: r.proveedor, saldo: money(r.saldo) })}</option>
        ))}
      </select>
    </div>
  );
}
export interface Cobro {
  id: string; fecha: string; cliente: string | null; monto: number; moneda: string;
  cuenta_id: string; forma_pago: string; concepto: string | null; documento_ref: string | null;
  estado: 'registrado' | 'anulado'; cuenta?: { alias: string | null; numero: string };
}
export interface FlujoCaja {
  saldo_actual: number; desde: string; hasta: string;
  items: { fecha: string; tipo: string; origen: string; detalle: string | null; concepto: string | null; monto: number; saldo_proyectado: number }[];
  total_egresos: number; total_ingresos: number; saldo_proyectado: number; saldo_minimo: number;
}
export const ESTADO_OP_CLS: Record<string, string> = {
  borrador: 'bg-muted text-muted-foreground', aprobada: 'bg-pendiente-soft text-pendiente',
  pagada: 'bg-estable-soft text-estable', anulada: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  rechazada: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

export interface RepConciliacion {
  cuenta_id: string; cuenta: string; banco: string; moneda: string;
  saldo_libro: number; saldo_extracto: number; mov_pendientes: number; lin_pendientes: number; ultima_conciliacion: string | null;
}
export interface RepFlujo {
  saldo_inicial: number; total_ingresos: number; total_egresos: number; flujo_neto: number; saldo_final: number;
  ingresos: { categoria: string; total: number }[]; egresos: { categoria: string; total: number }[];
}

// Exporta filas a CSV con protección contra inyección de fórmulas (=,+,-,@).
export function exportarCSV(nombre: string, headers: string[], filas: (string | number | null)[][]) {
  const esc = (v: string | number | null) => {
    let s = v == null ? '' : String(v);
    if (/^[=+\-@]/.test(s)) s = "'" + s;              // neutraliza fórmulas
    if (/[",\n;]/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
    return s;
  };
  const csv = [headers, ...filas].map((r) => r.map(esc).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${nombre}.csv`; a.click();
  URL.revokeObjectURL(url);
}

// hoy en local (evita corrimiento UTC de <input type=date>)
export const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
